import React from 'react';
import { X } from 'lucide-react';
import { prettyJson } from './messageLogUtils';
import './ExchangeViewer.css';

// ONE HTTP EXCHANGE, SHOWN THE SAME WAY WHEREVER IT IS SHOWN.
//
// Extracted from MessageLogPage, which is still its main caller. It moved here because the
// Reconciliation table needs the same four panes for a restricted user who cannot open the message log
// at all: the log carries every hop, every story line and every payload on the record, and that is more
// than "why did this one call fail". Copying the markup would have given the two views a chance to
// disagree about what a request looks like, which is the kind of difference nobody notices until
// somebody is reading the wrong one during an incident.
//
// The exchange shape is whatever oracle-ohip recorded: { method, url, status, requestHeaders, request,
// responseHeaders, response }. Headers are optional — older records have none — and a missing pane is
// omitted rather than rendered empty, because an empty box reads as "there were no headers" when it
// means "this record predates capturing them".
export const ExchangeViewer = ({ exchange }) => {
    if (!exchange) return null;
    const { method, url, status, requestHeaders, request, responseHeaders, response } = exchange;

    return (
        <div className="xv">
            <div className="xv-head">
                <span className="xv-method">{String(method || '').toUpperCase()}</span>
                <span className="xv-url">{url}</span>
                {status != null && (
                    <span className={`xv-status${Number(status) >= 400 ? ' xv-status-bad' : ''}`}>{status}</span>
                )}
            </div>

            <div className="xv-cols">
                <div className="xv-col">
                    {requestHeaders && (<>
                        <div className="xv-label">Request headers</div>
                        <pre className="xv-pre">{prettyJson(requestHeaders)}</pre>
                    </>)}
                    <div className="xv-label">Request body</div>
                    <pre className="xv-pre">{prettyJson(request)}</pre>
                </div>
                <div className="xv-col">
                    {responseHeaders && (<>
                        <div className="xv-label">Response headers</div>
                        <pre className="xv-pre">{prettyJson(responseHeaders)}</pre>
                    </>)}
                    <div className="xv-label">Response body</div>
                    <pre className="xv-pre">{prettyJson(response)}</pre>
                </div>
            </div>
        </div>
    );
};

// A LIGHTWEIGHT OVERLAY, which is what STYLE.md permits for a JSON viewer and only for a JSON viewer.
// It carries no form and no table, so none of the reasons detail views must be pages apply: there is
// nothing here to link to, nothing to come back to and nothing to lose by dismissing it.
export const ExchangeViewerOverlay = ({ title, subtitle, exchange, loading, error, onClose }) => (
    <div className="xv-overlay" onClick={onClose}>
        <div className="xv-overlay-panel" onClick={(e) => e.stopPropagation()}>
            <div className="xv-overlay-head">
                <div>
                    <h2 className="xv-overlay-title">{title || 'Failed request'}</h2>
                    {subtitle && <p className="xv-overlay-subtitle">{subtitle}</p>}
                </div>
                <button type="button" className="xv-overlay-close" onClick={onClose} title="Close">
                    <X size={16} />
                </button>
            </div>

            {loading && <div className="xv-overlay-msg">Loading the request…</div>}
            {/* An error and an empty transcript are never the same words. One is a page to retry, the
                other is a record whose failure happened before any call was made. */}
            {!loading && error && <div className="xv-overlay-msg xv-overlay-error">{error}</div>}
            {!loading && !error && !exchange && (
                <div className="xv-overlay-msg">
                    No request was recorded for this failure. The record failed before a call was sent.
                </div>
            )}
            {!loading && !error && exchange && <ExchangeViewer exchange={exchange} />}
        </div>
    </div>
);

export default ExchangeViewer;
